import { useState } from 'react'
import { AlertCircle, Check, ChevronDown, ChevronRight, Circle, Hourglass, Loader2, Minus } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import type { Plan, RunState } from '../v4/contracts'
import { stepAction } from '../v4/step-label'
import { nodeStatusDisplay } from '../statusDisplay'
import { AGENT_NAME } from './agentName'
import AgentDot from './AgentDot'
import { runStepStatus } from './runGraph'
import { stepEvidenceText, stepFailure } from './runStory'
import { AddLiveCard } from './AddLiveCard'
import { hasLiveCard, stepSeries, type LiveCardSpec, type StepSeries } from './liveCards'

/** Statuses after which a step will not move again. */
const SETTLED = new Set(['completed', 'completed_with_unknowns', 'failed', 'skipped', 'cancelled'])

function StepIcon({ status }: { status: string }) {
  const cls = 'mt-0.5 size-4 shrink-0'
  switch (status) {
    case 'completed':
      return <Check className={cn(cls, 'text-green-600 dark:text-green-500')} aria-hidden />
    case 'completed_with_unknowns':
      return <Check className={cn(cls, 'text-amber-600 dark:text-amber-500')} aria-hidden />
    case 'running':
      return <Loader2 className={cn(cls, 'animate-spin text-orange-600 dark:text-orange-500')} aria-hidden />
    case 'waiting_for_user':
      return <Hourglass className={cn(cls, 'text-amber-600 dark:text-amber-500')} aria-hidden />
    case 'failed':
    case 'cancelled':
      return <AlertCircle className={cn(cls, 'text-red-600 dark:text-red-500')} aria-hidden />
    case 'skipped':
      return <Minus className={cn(cls, 'text-muted-foreground')} aria-hidden />
    default:
      return <Circle className={cn(cls, 'text-muted-foreground/60')} aria-hidden />
  }
}

/**
 * The approved plan's steps as the run works through them (the old chat's step cards, as one
 * list): each step with the status its canvas card shows, a check and its one value or row count
 * once it has run, a spinner while it runs. The header pulses orange while a step is live, as the
 * old "Thinking..." line did, and settles to "Ran N steps". A row opens the step in the inspector.
 */
export default function StepsCard({
  run,
  plan,
  onSelectStep,
  liveCards = [],
  onAddLiveCard,
}: {
  run: RunState
  plan: Plan
  onSelectStep?: (stepId: string) => void
  /** The run's live cards, so a step whose series are already on one says so. */
  liveCards?: LiveCardSpec[]
  /** Adds a live card for the series a step read; without it, steps offer no such action. */
  onAddLiveCard?: (series: StepSeries, title: string, stepId?: string) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)
  const rows = plan.steps.map((step) => ({ step, status: runStepStatus(run, plan, step) }))
  const done = rows.filter((r) => SETTLED.has(r.status)).length
  const live = rows.some((r) => r.status === 'running' || r.status === 'waiting_for_user')
  const finished = run.status === 'finished' || done === rows.length
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 text-left text-sm font-medium',
          live ? 'text-orange-600 dark:text-orange-500' : 'text-muted-foreground'
        )}
      >
        <AgentDot />
        <span className={cn('min-w-0 flex-1 truncate', live && 'animate-pulse')}>
          {AGENT_NAME}:{' '}
          {finished
            ? t('workflow:builder.steps_done', { defaultValue: 'Ran {{count}} step(s)', count: rows.length })
            : t('workflow:builder.steps_running', { defaultValue: 'Running the plan' })}
        </span>
        {!finished && (
          <span className="shrink-0 text-xs font-normal text-muted-foreground">
            {t('workflow:builder.steps_count', { defaultValue: '{{done}} of {{total}} done', done, total: rows.length })}
          </span>
        )}
        <Chevron className="size-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <ol className="mt-2 overflow-hidden rounded-lg border border-border">
          {rows.map(({ step, status }) => {
            const display = nodeStatusDisplay(status)
            const evidence = stepEvidenceText(run, plan, step.id, (count) =>
              t('workflow:builder.rows', { defaultValue: '{{count}} row(s)', count })
            )
            // A settled step shows what it found; one still to run shows what it will do.
            const detail = evidence ?? (SETTLED.has(status) ? null : stepAction(step))
            // A step that failed or waits says why, when the API carries the reason.
            const failure = status === 'failed' || status === 'waiting_for_user' ? stepFailure(run, step.id) : null
            return (
              <li key={step.id} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  disabled={!onSelectStep}
                  onClick={() => onSelectStep?.(step.id)}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent dark:hover:bg-dark-muted/50"
                >
                  <StepIcon status={status} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-foreground">{step.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t(display.labelKey, { defaultValue: display.label })}
                      {detail ? ` · ${detail}` : ''}
                    </span>
                    {failure && (
                      <span className="block truncate text-xs text-red-600 dark:text-red-400" title={failure}>
                        {failure}
                      </span>
                    )}
                  </span>
                </button>
                {onAddLiveCard && status !== 'failed' && SETTLED.has(status) && (
                  <div className="flex flex-wrap gap-2 pb-2 pl-[2.375rem] pr-3">
                    {stepSeries(run, step).map((series) => (
                      <AddLiveCard
                        key={series.documentId}
                        series={series}
                        added={hasLiveCard(liveCards, series)}
                        onAdd={(s) => onAddLiveCard(s, step.title, step.id)}
                      />
                    ))}
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
